import React, { useState } from 'react';
import { isAdmin } from '../../services/api';
import GlobalShorthand from './GlobalShorthand';
import MessageCorrections from './MessageCorrections';
import QaKnowledge from './QaKnowledge';

// Global knowledge — now a 3-sub-tab shell over the v1 global-curation surfaces.
// The old single-page layout (global shorthand + global NICKNAMES + Q&A browse)
// is split into three focused sub-tabs. Global NICKNAMES were removed entirely —
// per Michael, a nickname is inherently per-customer, so a "global nickname"
// makes no sense (its state, handlers, and the editNickname/deactivateNickname
// imports are gone).
//   1. Shorthand aliases — the promoted-to-general shorthand table.
//   2. Message corrections — AI draft vs. what a human actually sent/discarded.
//   3. Q&A knowledge — the qa_knowledge_base browse + learned experience.
//
// Sub-tab strip mirrors the top-level AIInfo.tsx tab pattern. The isAdmin()
// early-return guard is preserved (belt-and-suspenders; the parent already gates).

type SubTabKey = 'shorthand' | 'corrections' | 'qa';

const SUB_TABS: { key: SubTabKey; label: string }[] = [
  { key: 'shorthand', label: 'Shorthand aliases' },
  { key: 'corrections', label: 'Message corrections' },
  { key: 'qa', label: 'Q&A knowledge' },
];

const GlobalKb: React.FC = () => {
  const [tab, setTab] = useState<SubTabKey>('shorthand');

  // UX-only admin gate (the parent shell also gates — defensive per contract).
  if (!isAdmin()) return null;

  return (
    <div className="flex-1 flex flex-col">
      {/* Sub-tab strip — mirrors the top-level AIInfo.tsx tab-strip styling. */}
      <div className="bg-white px-3 sm:px-6 pt-2 border-b border-gray-200">
        <div className="flex items-center gap-1">
          {SUB_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 ' +
                (tab === t.key
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active sub-view. Each owns its own load/error state and scroll area. */}
      {tab === 'shorthand' && <GlobalShorthand />}
      {tab === 'corrections' && <MessageCorrections />}
      {tab === 'qa' && <QaKnowledge />}
    </div>
  );
};

export default GlobalKb;
